import { motion, AnimatePresence } from 'framer-motion';

/**
 * A clear "glass" that fills up with items (fruits, capsules, dumbbells)
 * based on progress vs goal. Items enter from above and settle.
 *
 * Props:
 *  - progress: number  (current value, e.g. 1200 calories or 2 vitamins)
 *  - goal: number      (target, e.g. 2000 calories)
 *  - emoji: string     ("🍎" | "💊" | "🏋️")
 *  - label: string     (unit label under number)
 *  - accent: string    (color for fill)
 */
const GlassTracker = ({ progress = 0, goal = 100, emoji = '🍎', label = '', accent = '#4A7C59' }) => {
  const pct = Math.max(0, Math.min(1, goal ? progress / goal : 0));
  // Number of visual items proportional to progress (capped at 24 for perf)
  const target = Math.min(24, Math.max(0, Math.round(pct * 24)));
  const items = Array.from({ length: target });

  return (
    <div className="flex flex-col items-center select-none">
      <div className="mb-4 text-center">
        <div className="text-5xl font-serif-display font-medium text-[#1A2421] tabular-nums" data-testid="glass-progress-value">
          {Math.round(progress)}
        </div>
        <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53] mt-1">
          of {Math.round(goal)} {label}
        </div>
      </div>

      <div className="relative w-56 h-72" aria-hidden="true">
        {/* Glass outline (SVG) */}
        <svg viewBox="0 0 200 260" className="absolute inset-0 w-full h-full">
          <defs>
            <linearGradient id="glassGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ffffff" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#ffffff" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          {/* Glass body: slightly tapered */}
          <path
            d="M35 20 L165 20 L155 240 Q100 254 45 240 Z"
            fill="url(#glassGrad)"
            stroke="#B9AE95"
            strokeWidth="2"
          />
          {/* Fill overlay based on progress */}
          <clipPath id="glassClip">
            <path d="M35 20 L165 20 L155 240 Q100 254 45 240 Z" />
          </clipPath>
          <rect
            x="0"
            y={260 - pct * 220 - 20}
            width="200"
            height={pct * 220}
            fill={accent}
            fillOpacity="0.14"
            clipPath="url(#glassClip)"
          />
          {/* Highlight streak */}
          <path d="M45 30 L52 220" stroke="#ffffff" strokeOpacity="0.8" strokeWidth="3" strokeLinecap="round" />
        </svg>

        {/* Falling items pile up from bottom */}
        <div className="absolute inset-0 pointer-events-none">
          <AnimatePresence initial={false}>
            {items.map((_, i) => {
              // 5 columns × up to 5 rows stacking pattern
              const col = i % 5;
              const row = Math.floor(i / 5);
              const left = 20 + col * 22 + (row % 2) * 6; // slight offset per row
              const bottom = 24 + row * 26;
              return (
                <motion.span
                  key={`${target}-${i}`}
                  initial={{ y: -260, opacity: 0, rotate: -15 }}
                  animate={{ y: 0, opacity: 1, rotate: 0 }}
                  transition={{
                    y: { type: 'spring', stiffness: 140, damping: 14, delay: i * 0.04 },
                    opacity: { duration: 0.2, delay: i * 0.04 },
                  }}
                  className="absolute text-2xl"
                  style={{ left: `${left}px`, bottom: `${bottom}px` }}
                >
                  {emoji}
                </motion.span>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-3 h-1.5 w-56 rounded-full bg-[#E5E1D8] overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: accent }}
          initial={false}
          animate={{ width: `${pct * 100}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
    </div>
  );
};

export default GlassTracker;
